import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import latinize from 'latinize';
import { InfoFeedClient } from '../../helpers/client';
import { InfoFeed, InfoFeedOverview } from 'orderme-api-integration-client';
import { BreadcrumbsBar } from '../breadcrumbs/breadcrumbsBar';
import { NavBar } from '../appBars/navBar/navBar';
import { useTranslation } from 'react-i18next';
import { FaqRoute } from '../../routes/allRoutes';
import { HtmlViewer } from '../../controls/html/htmlViewer';
import { appStyleMode } from '../../helpers/clientConfigs';
import { AppStyle } from '../../helpers/appStyle';

export function FaqList() {
  const [questions, setQuestions] = useState<InfoFeedOverview[]>([]);
  const [details, setDetails] = useState<Record<string, InfoFeed>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const faqClient = useMemo(() => new InfoFeedClient(), []);
  const { t } = useTranslation();

  const filteredQuestions = useMemo(() => {
    const q = latinize(searchQuery.trim()).toLowerCase();
    if (!q) {
      return questions;
    }
    return questions.filter(
      (item) =>
        latinize(item.title ?? '')
          .toLowerCase()
          .includes(q) ||
        latinize(item.heading ?? '')
          .toLowerCase()
          .includes(q)
    );
  }, [questions, searchQuery]);

  const fetchQuestions = useCallback(async () => {
    try {
      const response = await faqClient.getQuestions();
      setQuestions(response);
    } catch (error) {
      console.error('Error fetching FAQ questions:', error);
    }
  }, [faqClient]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const handleExpand = async (item: InfoFeedOverview, expanded: boolean) => {
    setExpandedId(expanded ? item.id : null);
    if (!expanded || details[item.id]) {
      return;
    }

    setLoading((prev) => ({ ...prev, [item.id]: true }));
    try {
      const feed = await faqClient.get(item.id);
      setDetails((prev) => ({ ...prev, [item.id]: feed }));
    } catch (error) {
      console.error(`Error fetching FAQ detail for ID=${item.id}:`, error);
    } finally {
      setLoading((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <NavBar barTitle={t('faq:menuTitle')} />
      <BreadcrumbsBar paths={[{ path: FaqRoute }]} />
      <Box
        sx={{
          backgroundColor: 'white',
          color: 'black',
          width: '100%',
          borderRadius: '15px',
        }}
      >
        <Box className={appStyleMode === AppStyle.Mobile && 'faq-list'} sx={{ p: { xs: '10px', md: '20px' } }}>
          {appStyleMode !== AppStyle.Mobile && (
            <Typography variant="h5" fontWeight="bold" sx={{ mb: '10px' }}>
              {t('faq:menuTitle')}
            </Typography>
          )}
          <TextField
            fullWidth
            size="small"
            placeholder={t('faq:searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{
              input: {
                sx: { borderRadius: '10px' },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          {filteredQuestions.map((item) => (
            <Accordion
              key={item.id}
              expanded={expandedId === item.id}
              onChange={(_e, expanded) => handleExpand(item, expanded)}
              sx={{ mt: '10px', borderRadius: '10px !important', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography fontWeight="bold">{item.title}</Typography>
                  {item.heading && (
                    <Typography variant="body2" color="text.secondary">
                      {item.heading}
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {loading[item.id] ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <HtmlViewer content={details[item.id]?.description ?? ''} />
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
